import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { manualUrl } from "@/components/HelpButton";
import FaqEditDialog from "./FaqEditDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" }) : "—");

export default function FaqEntriesPanel({ refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/admin/help/faq")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const remove = async (entry) => {
    if (!window.confirm(`FAQ-regel "${entry.question}" verwijderen uit de handleiding?`)) return;
    try {
      const { data: d } = await api.delete(`/admin/help/faq/${entry.id}`);
      toast.success(d.removed_from_manual ? "FAQ-regel verwijderd uit de handleiding." : "Registratie verwijderd (de regel stond niet meer in de handleiding).");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="faq-entries-panel">
      <p className="text-[11px] text-muted-foreground px-3 py-2 border-b border-border">FAQ-regels die via de chat zijn toegevoegd aan <a href={manualUrl("faq")} target="_blank" rel="noreferrer" className="underline">Veelgestelde vragen</a>. Bewerken of verwijderen past de handleiding direct aan.</p>
      {!data ? <div className="p-3 text-sm text-muted-foreground">Laden…</div> : data.entries.length === 0 ? (
        <div className="p-3 text-sm text-muted-foreground" data-testid="faq-entries-empty">Nog geen FAQ-regels via de chat toegevoegd.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="faq-entries-table">
            <thead className="bg-foreground text-background">
              <tr>{["Vraag", "Antwoord", "Toegevoegd", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.entries.map((e, i) => (
                <tr key={e.id} className="border-t border-border even:bg-muted/30" data-testid={`faq-entry-row-${i}`}>
                  <td className="px-3 py-2 max-w-[300px] font-bold"><div className="line-clamp-2" title={e.question}>{e.question}</div>{e.source_question && <div className="text-[11px] font-normal text-muted-foreground truncate" title={e.source_question}>uit chat: {e.source_question}</div>}</td>
                  <td className="px-3 py-2 max-w-[360px] text-xs text-muted-foreground"><div className="line-clamp-2" title={e.answer}>{e.answer}</div></td>
                  <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{fmt(e.added_at)}{e.updated_at && <div className="text-muted-foreground">bewerkt {fmt(e.updated_at)}</div>}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {e.in_manual ? <span className="inline-flex items-center gap-1 text-positive" data-testid={`faq-entry-status-${i}`}><CheckCircle2 className="w-3.5 h-3.5" /> In handleiding</span>
                      : <span className="inline-flex items-center gap-1 text-destructive" title="De regel staat niet (meer) in het hoofdstuk Veelgestelde vragen" data-testid={`faq-entry-status-${i}`}><AlertTriangle className="w-3.5 h-3.5" /> Niet gevonden</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(e)} className="p-1.5 hover:bg-muted" title="Bewerken" data-testid={`faq-entry-edit-${i}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(e)} className="p-1.5 hover:bg-destructive/10 text-destructive" title="Verwijderen" data-testid={`faq-entry-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FaqEditDialog entry={editing} onClose={() => setEditing(null)} onSaved={() => { load(); onChanged?.(); }} />
    </div>
  );
}
